import { PlusOutlined } from "@ant-design/icons";
import { Alert, Button } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    BeaconPositionEvent,
    FromToFilter,
    Position,
    usePositionHistory,
} from "../../../api";
import { useSCBOneOperationRunPaginated } from "../../../api/api.scb-one";
import { PagedApiState } from "../../../api/useApiState";
import { BaseCard } from "../../BaseCard";
import { SelectedOperationRun } from "../../details/SCBOneDetails";
import {
    BeaconPositionDrawer,
    OperationRunPositionsDrawer,
} from "./PositionDrawer";
import { PositionTable } from "./PositionTable";

interface SCBOneOperationRunPositionCardProps {
  operationRun: SelectedOperationRun | undefined;
  allOperationRunPositions: Position[];
  allOperationRunPositionsLoading: boolean;
}
export function SCBOneOperationRunPositionCard({
  operationRun,
  allOperationRunPositions,
  allOperationRunPositionsLoading,
}: SCBOneOperationRunPositionCardProps) {
  const { t } = useTranslation();
  const [operationRunPosDrawerVisible, setOperationRunPosDrawerVisible] =
    useState(false);

  const operationRunPositions = useSCBOneOperationRunPaginated(
    operationRun?.runId,
    operationRun?.serial
  );
  return (
    <BaseCard
      title={t("scb-one:operation-run-positions-title")}
      allowScroll={true}
    >
      {operationRun ? (
        <PositionTable
          positions={operationRunPositions}
          isLoading={operationRunPositions.loading}
          visibleColumns={["ts", "lat", "lon", "hAcc", "vAcc", "cprs"]}
        />
      ) : (
        <Alert
          message={t("scb-one:run-notification-select")}
          type="info"
          showIcon
        />
      )}
      <OperationRunPositionsDrawer
        operationRun={operationRun}
        paginatedOperationRunPositions={operationRunPositions}
        allOperationRunPositions={allOperationRunPositions}
        allOperationRunPositionsLoading={allOperationRunPositionsLoading}
        isLoading={operationRunPositions.loading}
        operationRunPosDrawerVisible={operationRunPosDrawerVisible}
        onCloseDrawer={(visible) => setOperationRunPosDrawerVisible(visible)}
      />
      <Button
        className="show-card-drawer-button"
        type="primary"
        onClick={() => setOperationRunPosDrawerVisible(true)}
        icon={<PlusOutlined />}
      />
    </BaseCard>
  );
}

interface BeaconPositionCardProps {
  serial: string;
  selectedFromToFilter: FromToFilter & { from?: Date; to?: Date };
}
export function BeaconPositionCard({
  serial,
  selectedFromToFilter,
}: BeaconPositionCardProps) {
  const { t } = useTranslation();
  const [drawerVisible, setDrawerVisible] = useState(false);

  const positionHistory = usePositionHistory(serial, selectedFromToFilter);

  function transformToTableData(
    positionData: PagedApiState<BeaconPositionEvent>
  ): PagedApiState<Position> {
    return {
      ...positionHistory,
      value: {
        totalCount: positionData.value?.totalCount || 0,
        items:
          positionData.value?.items?.map((item: BeaconPositionEvent) => ({
            ts: item.ts,
            lat: item.position?.lat,
            lon: item.position?.lon,
            alt: item.position?.alt,
            acc: item.position?.acc,
            sats: item.position?.sats,
            cprs: item.position?.cprs,
            dop: item.position?.dop,
          })) || [],
      },
    };
  }

  const transformedPositionHistory = useMemo(() => {
    return transformToTableData(positionHistory);
  }, [positionHistory]);

  return (
    <BaseCard title={t("positions")} allowScroll={true}>
      <PositionTable
        positions={transformedPositionHistory}
        isLoading={positionHistory.loading}
        visibleColumns={["ts", "lat", "lon", "hAcc", "vAcc", "cprs"]}
      />
      <BeaconPositionDrawer
        positions={transformedPositionHistory}
        drawerVisible={drawerVisible}
        onCloseDrawer={() => setDrawerVisible(false)}
      />
      <Button
        className="show-card-drawer-button"
        type="primary"
        onClick={() => setDrawerVisible(true)}
        icon={<PlusOutlined />}
      />
    </BaseCard>
  );
}
